import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { paper, fonts, easing, shade, inkA, gloss } from "../theme";
import { useWebSocket } from "../useWebSocket";
import * as haptics from "../haptics";
import type { PlayerInfo, PromptEntryView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsBtn, MsWaitingFor,
    MaskHeadline,
    CodeInput,
    Centered,
} from "../ms";
import { PromptCardBack } from "./PromptCardBack";
import { CARD_RATIO, cardTransition } from "./promptCard";
import { PromptReveal, CardGlyph, CardBorderFrame } from "./PromptReveal";


/* ── Picker view — the prompt card + draw ─────────────────────────────
 *
 * The card mirrors the physical printed one (see the backend's
 * admin/cards-print.html): a landscape card with an inset border frame, a
 * faint red quote glyph behind, the three-letter code in boxed cells top-right,
 * and the prompt centred over a short rule. Its code cells echo the CodeInput
 * below, so "the code is up here → type it down there" reads at a glance.
 *
 * The card is a real, intact card — no deckle. Prompt cards are die-cut, not
 * torn; the deckle belongs to the game's paper scraps, not its deck.
 */

const PickerColumn = styled(motion.div)`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const DrawStage = styled.div`
    position: relative;
    width: 100%;
    max-width: 340px;
    margin: 6px auto 0;
    aspect-ratio: ${CARD_RATIO};
`;

/* Receded cards suggesting the deck the drawn card came off. Purely a backdrop:
 * offset, tilted, and dimmed so the front card reads as lifted clear of them.
 * They fade in first (staggered), then the top card deals off — so the deck
 * assembles before it's drawn from. Only opacity is animated, never transform,
 * so the per-card offset/tilt below is left untouched. */
const deckIn = keyframes`
    from { opacity: 0; }
    to   { opacity: 0.9; }
`;

const DeckCard = styled.div<{ tilt: number; dx: number; dy: number; delay: number }>`
    position: absolute;
    inset: 0;
    background: ${paper.paperCool};
    border-radius: 11px;
    box-shadow:
        0 0 0 1px ${shade(0.06)},
        0 6px 14px -8px ${shade(0.32)};
    transform: translate(${p => p.dx}px, ${p => p.dy}px) rotate(${p => p.tilt}deg) scale(0.975);
    opacity: 0.9;
    animation: ${deckIn} 0.4s ${easing.standard} ${p => p.delay}s both;

    &::after {
        content: '';
        position: absolute;
        inset: 9px;
        border: 1.2px solid ${shade(0.12)};
        border-radius: 6px;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

/* The card is dealt off the deck on mount: it rises from behind the stack with
 * a small overshoot and settles square.
 *
 * A CSS keyframe, deliberately not framer: the screen's AnimatePresence runs
 * `initial={false}` (PageTransition owns the screen's entrance), which would
 * suppress a framer enter-animation on exactly the mount we care about. CSS
 * runs on mount regardless. It lives on this wrapper, not the card, so it never
 * fights the layoutId morph — the wrapper settles to identity well before the
 * card FLIPs to the headline. `transform-origin` low so it pivots up off the
 * deck rather than spinning about its middle. */
const dealIn = keyframes`
    0%   { opacity: 0; transform: translateY(34px) rotate(-6deg) scale(0.9); }
    58%  { opacity: 1; transform: translateY(-5px) rotate(1.4deg) scale(1.012); }
    100% { opacity: 1; transform: translateY(0) rotate(0) scale(1); }
`;

/* Delayed a beat so the deck fades in first, then the top card lifts off it. */
const DealWrap = styled.div`
    position: absolute;
    inset: 0;
    transform-origin: 50% 85%;
    animation: ${dealIn} 0.66s ${easing.pop} 0.18s both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

/* Ambient hold, for the non-picker's wait: once dealt, the face-down card keeps
 * a slow life — lifting and swaying as if held and turned over while the picker
 * decides. Deliberately *not* a short repeating loop (the mechanical shuffle
 * this replaces): a long, uneven, multi-key path that returns to rest, so the
 * eye never catches a cadence. Starts at rest (0% == 100% == identity) so a
 * paused frame reads correctly, and it's an ancestor of the card so it composes
 * with — rather than fights — the layoutId morph on reveal. */
const sway = keyframes`
    0%   { transform: translateY(0)    rotate(0deg); }
    21%  { transform: translateY(-6px) rotate(1.4deg); }
    43%  { transform: translateY(-10px) rotate(-0.4deg); }
    60%  { transform: translateY(-5px) rotate(-1.5deg); }
    82%  { transform: translateY(-8px) rotate(0.5deg); }
    100% { transform: translateY(0)    rotate(0deg); }
`;

const AmbientHold = styled.div`
    position: absolute;
    inset: 0;
    transform-origin: 50% 88%;
    animation: ${sway} 10.5s ${easing.standard} 1s infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

/**
 * The drawn card, dealt off a small deck. Shared by both the picker (a face-up
 * prompt card) and the non-picker (a face-down back) so the draw reads
 * identically for everyone; only the card handed in as `children` differs.
 *
 * `ambient` gives the non-picker's card its ongoing hold (see AmbientHold) —
 * something to watch while they wait — without touching the picker's, which
 * must sit still under its code and annotation.
 */
function DrawDeck({ children, ambient }: { children: ReactNode; ambient?: boolean }) {
    return (
        <DrawStage>
            <DeckCard tilt={3.4} dx={10} dy={12} delay={0.02} />
            <DeckCard tilt={-2.6} dx={-8} dy={6} delay={0.1} />
            <DealWrap>{ambient ? <AmbientHold>{children}</AmbientHold> : children}</DealWrap>
        </DrawStage>
    );
}

const PromptCard = styled(motion.div)`
    position: absolute;
    inset: 0;
    background: ${paper.paperCool};
    border-radius: 11px;
    box-shadow:
        0 1px 0 ${gloss(0.6)} inset,
        0 0 0 1px ${shade(0.07)},
        0 10px 22px -10px ${shade(0.4)},
        0 24px 36px -22px ${shade(0.45)};
    overflow: hidden;
`;



const CardCode = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    display: flex;
    gap: 5px;
    z-index: 2;
`;

const CardCodeCell = styled.div`
    width: 21px;
    aspect-ratio: 0.76;
    border-radius: 3px;
    box-shadow: 0 0 0 1.4px ${inkA(0.62)};
    display: flex;
    align-items: center;
    justify-content: center;

    span {
        font-family: ${fonts.serif};
        font-weight: 700;
        font-size: 14px;
        line-height: 1;
        color: ${paper.ink};
        letter-spacing: -0.01em;
        font-variant-numeric: tabular-nums;
    }
`;

/* A red-ink annotation looped around the code — the paper-desk way to say
 * "this bit, here". It draws itself on once the card has settled. The loop is a
 * deliberately loose, over-shooting ellipse so it reads as hand-drawn rather
 * than printed. */
const inkDraw = keyframes`
    from { stroke-dashoffset: 260; }
    to   { stroke-dashoffset: 0; }
`;

const CodeCircle = styled.svg`
    position: absolute;
    top: 2px;
    right: 1px;
    width: 108px;
    height: 58px;
    z-index: 3;
    overflow: visible;
    pointer-events: none;
    transform: rotate(-3deg);

    path {
        fill: none;
        stroke: ${paper.red};
        stroke-width: 2.4;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-dasharray: 260;
        animation: ${inkDraw} 0.72s ${easing.standard} 0.9s both;
    }

    @media (prefers-reduced-motion: reduce) {
        path { animation: none; stroke-dashoffset: 0; }
    }
`;

const CardPromptArea = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px 24px;
    text-align: center;
    z-index: 1;
`;

const CardPrompt = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 19px;
    line-height: 1.2;
    letter-spacing: -0.008em;
    color: ${paper.ink};
    text-wrap: balance;
    max-width: 20ch;
`;

const CardBlank = styled.span`
    color: ${paper.red};
    letter-spacing: 0.04em;
`;

const CardPromptRule = styled.div`
    width: 44px;
    height: 2px;
    border-radius: 1px;
    background: ${paper.ink};
    margin-top: 13px;
`;

const PickerDisplay = styled(MsDisplay)`
    font-size: 26px;
`;

const PickerHint = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    color: ${paper.inkSoft};
    font-size: 14px;
`;

const CodeInputWrap = styled.div`
    max-width: 240px;
    margin: 4px auto 0;
    width: 100%;
`;

/* ── Waiting view — the same draw, but a face-down card ───────────────── */
const WaitingColumn = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
`;

/* The face-down card the non-picker watches being drawn lives in
 * `PromptCardBack` — the deckless draw deals three of the same object, and one
 * card back is enough for the app to have. */

const WaitingDisplay = styled(MsDisplay)`
    font-size: 26px;
    text-align: center;
`;

/** Sample card shown to the picker — illustrative, not their actual card. The
 *  code marks where to look on the card in their hand; the prompt shows the
 *  house style with its blank. */
const SAMPLE_CODE = ["N", "Y", "R"];

/** Split a prompt on its `___` blank so the blank can be styled as a fill-in
 *  rather than three literal underscores. */
function renderPromptBlank(text: string) {
    const parts = text.split(/_{2,}/);
    return parts.flatMap((part, i) =>
        i === 0 ? [part] : [<CardBlank key={i}>&nbsp;____&nbsp;</CardBlank>, part],
    );
}

interface Props {
    view: PromptEntryView;
    self: PlayerInfo | null;
}

/**
 * Three phases share one screen, with a shared `layoutId="prompt-card"` element
 * that morphs between them:
 *   - waiting (non-picker): a card dealt face-down off the deck
 *   - picker: the same draw, face-up, showing a placeholder prompt + its code
 *   - reveal: the code has landed; the card grows to the fore and shows the
 *     real prompt — no chrome, just the game card, bigger
 *
 * The drawn card (face-up for the picker, face-down for everyone else) grows
 * into the reveal card when `promptText` arrives, both sharing layoutId
 * "prompt-card". Surrounding chrome (code input, "dealer is drawing", footer
 * copy) cross-fades via AnimatePresence.
 */
export default function PromptEntry({ view, self }: Props) {
    const { sendMessage } = useWebSocket();
    const [code, setCode] = useState("");

    const { picker, promptText } = view;
    const isPicker = picker?.id === self?.id;
    const dealerName = picker?.name || "someone";
    const ready = code.length === 3;

    /* The round has been handed to you. This is the one haptic the server fires
     * rather than your thumb, and the one that has to land on a phone nobody is
     * looking at — everyone else is watching whoever holds the deck. */
    useEffect(() => {
        if (isPicker) haptics.yourTurn();
    }, [isPicker]);

    const submit = () => {
        if (ready) sendMessage({ type: "submit_prompt_code", code: code.trim().toUpperCase() });
    };

    const fadeTransition = { duration: 0.32, ease: [0.45, 0, 0.2, 1] as const };

    /* ── Reveal view (prompt revealed) ────────────────────────────────── */
    const renderHeadline = () => <PromptReveal key="headline" promptText={promptText!} />;

    /* ── Picker view (drawing + entering code) ────────────────────────── */
    const renderPicker = () => (
        <PickerColumn
            key="picker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: fadeTransition }}
            exit={{ opacity: 0, transition: { duration: 0.18 } }}
        >
            <DrawDeck>
                <PromptCard layoutId="prompt-card" transition={cardTransition}>
                    <CardGlyph>"</CardGlyph>
                    <CardBorderFrame />
                    <CardCode>
                        {SAMPLE_CODE.map((ch, i) => (
                            <CardCodeCell key={i}><span>{ch}</span></CardCodeCell>
                        ))}
                    </CardCode>
                    <CodeCircle viewBox="0 0 108 58" aria-hidden="true">
                        <path d="M 92,10 C 66,2 26,3 12,20 C 3,32 14,48 46,50 C 80,52 104,40 98,20 C 94,10 82,7 70,8" />
                    </CodeCircle>
                    <CardPromptArea>
                        <CardPrompt>{renderPromptBlank("the ___ of all misunderstandings")}</CardPrompt>
                        <CardPromptRule />
                    </CardPromptArea>
                </PromptCard>
            </DrawDeck>

            <Centered>
                <PickerDisplay>
                    <MaskHeadline>Draw a card from the deck.</MaskHeadline>
                </PickerDisplay>
                <PickerHint>
                    Type the three-letter code from your card's top-right corner.
                </PickerHint>
            </Centered>

            <CodeInputWrap>
                <CodeInput value={code} onChange={setCode} length={3} label="Prompt code" />
            </CodeInputWrap>
        </PickerColumn>
    );

    /* ── Non-picker waiting view (a card drawn face-down) ─────────────── */
    const renderWaiting = () => (
        <WaitingColumn
            key="waiting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: fadeTransition }}
            exit={{ opacity: 0, transition: { duration: 0.18 } }}
        >
            <DrawDeck ambient>
                <PromptCardBack layoutId="prompt-card" transition={cardTransition} />
            </DrawDeck>
            <WaitingDisplay>
                <MaskHeadline>{dealerName} is drawing.</MaskHeadline>
            </WaitingDisplay>
        </WaitingColumn>
    );

    const showHeadline = !!promptText;

    return (
        <MsScreen>
            <MsBody style={{
                justifyContent: showHeadline ? "center" : "flex-start",
                gap: 18,
                paddingTop: showHeadline ? 56 : 20,
            }}>
                <AnimatePresence mode="popLayout" initial={false}>
                    {showHeadline
                        ? renderHeadline()
                        : isPicker ? renderPicker() : renderWaiting()}
                </AnimatePresence>
            </MsBody>
            <MsFooter>
                {/* No footer once the prompt is revealed — the card is the whole
                    moment; a disabled status bar only competes with it. */}
                <AnimatePresence mode="wait" initial={false}>
                    {showHeadline ? null : isPicker ? (
                        <motion.div
                            key="picker-cta"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0, transition: fadeTransition }}
                            exit={{ opacity: 0, y: 6, transition: { duration: 0.15 } }}
                        >
                            <MsBtn variant="red" disabled={!ready} onClick={submit}>Reveal prompt</MsBtn>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="waiting-cta"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0, transition: fadeTransition }}
                            exit={{ opacity: 0, y: 6, transition: { duration: 0.15 } }}
                        >
                            <MsWaitingFor name={dealerName} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </MsFooter>
        </MsScreen>
    );
}
